// src/App.js

import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { Spin } from "antd"; // Import Spin for loading state
import axios from "axios"; // Import axios
import { BASE_URL } from "./api"; // Assuming you have this file for your base URL

// Import your components
import CurrentPositions from "./components/CurrentPositions";
import OrdersPage from "./components/OrdersPage";
import HeaderMenu from "./components/HeaderMenu";
import LoginPage from "./components/LoginPage";
import CloneOrders from './components/CloneOrders';

// This component now manages routing based on the user object
function AppRoutes({ user, setUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = !!user; // Authentication status is derived from the user object

  useEffect(() => {
    // Redirect to login if not authenticated and not on the login page
    if (!isAuthenticated && location.pathname !== "/login") {
      navigate("/login");
    }
    // Redirect to orders if authenticated and on the login page
    if (isAuthenticated && location.pathname === "/login") {
      navigate("/orders");
    }
  }, [isAuthenticated, location, navigate]);

  // NEW LOGOUT HANDLER
  const handleLogout = async () => {
    try {
      await axios.post(`${BASE_URL}/api/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setUser(null); // Clear user state regardless of API call success
      navigate("/login");
    }
  };

  return (
    <>
      {isAuthenticated && <HeaderMenu onLogout={handleLogout} />}
      <div style={{ paddingTop: isAuthenticated ? 70 : 0 }}>
        <Routes>
          <Route
            path="/login"
            element={
              <LoginPage onLogin={(loggedInUser) => setUser(loggedInUser)} />
            }
          />
          <Route
            path="/current-positions"
            element={isAuthenticated ? <CurrentPositions /> : null}
          />
          <Route
            path="/orders"
            element={isAuthenticated ? <OrdersPage /> : null}
          />
          <Route
            path="/clone-orders"
            element={isAuthenticated ? <CloneOrders /> : null}
          />
          {/* Default route redirects to orders if logged in, otherwise login */}
          <Route
            path="*"
            element={
              isAuthenticated ? (
                <OrdersPage />
              ) : (
                <LoginPage onLogin={(loggedInUser) => setUser(loggedInUser)} />
              )
            }
          />
        </Routes>
      </div>
    </>
  );
}

// The main App component now handles the master user state and session check

function App() {
  const [user, setUser] = useState(null); // Single state for user object
  const [loading, setLoading] = useState(true); // Loading state for initial auth check

  // NEW LOGIC: Check for session on initial app load with timeout and fallback
  useEffect(() => {
    const checkUserSession = async () => {
      try {
        // Add timeout to prevent hanging for too long
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

        const res = await axios.get(`${BASE_URL}/api/check-auth`, {
          withCredentials: true,
          timeout: 5000, // Additional axios timeout
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (res.data && res.data.success) {
          setUser(res.data.user); // Set user from valid session
        }
      } catch (error) {
        if (error.name === 'AbortError' || error.code === 'ECONNABORTED') {
          console.log("Auth check timed out - proceeding to login page");
        } else {
          console.log("No active session found:", error.message);
        }
      } finally {
        setLoading(false); // End loading after check completes
      }
    };

    // ✅ FALLBACK: If auth check takes too long, stop loading after 3 seconds
    const fallbackTimeout = setTimeout(() => {
      console.log("Auth check taking too long - showing login page");
      setLoading(false);
    }, 3000);

    checkUserSession().finally(() => {
      clearTimeout(fallbackTimeout);
    });
  }, []); // Empty array ensures this runs only once

  // Username-based .pnl-numbers display logic (use user from state)
  useEffect(() => {
    const username = user && user.username ? user.username : "";
    let styleTag = document.getElementById("user-style");
    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = "user-style";
      document.head.appendChild(styleTag);
    }
    if (username === "admin") {
      styleTag.innerHTML = `.pnl-numbers { display: inline-block !important; }`;
    } else {
      styleTag.innerHTML = `.pnl-numbers { display: none !important; }`;
    }
  }, [user]);

  // While checking the session, show a loading spinner
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Router>
      <AppRoutes user={user} setUser={setUser} />
    </Router>
  );
}

export default App;

// import React, { useEffect, useState } from "react";
// import {
//   BrowserRouter as Router,
//   Route,
//   Routes,
//   useNavigate,
//   useLocation,
// } from "react-router-dom";
// import CurrentPositions from "./components/CurrentPositions";
// import OrdersPage from "./components/OrdersPage";
// import HeaderMenu from "./components/HeaderMenu";
// import LoginPage from "./components/LoginPage";

// function AppRoutes({ isAuthenticated, setIsAuthenticated, setUsername }) {
//   const navigate = useNavigate();
//   const location = useLocation();

//   useEffect(() => {
//     if (!isAuthenticated && location.pathname !== "/login") {
//       navigate("/login");
//     }
//     if (isAuthenticated && location.pathname === "/login") {
//       navigate("/orders");
//     }
//   }, [isAuthenticated, location, navigate]);

//   // Logout handler
//   const handleLogout = () => {
//     // Remove username cookie
//     document.cookie =
//       "username=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
//     setIsAuthenticated(false);
//     setUsername("");
//     navigate("/login");
//   };

//   return (

//     <>

//       {isAuthenticated && <HeaderMenu onLogout={handleLogout} />}
//       <div style={{ paddingTop: isAuthenticated ? 70 : 0 }}>
//         <Routes>
//           <Route
//             path="/login"
//             element={
//               <LoginPage
//                 onLogin={(username) => {
//                   setIsAuthenticated(true);
//                   setUsername(username);
//                 }}
//               />
//             }
//           />
//           <Route
//             path="/current-positions"
//             element={isAuthenticated ? <CurrentPositions /> : null}
//           />
//           <Route
//             path="/orders"
//             element={isAuthenticated ? <OrdersPage /> : null}
//           />
//           <Route
//             path="*"
//             element={
//               isAuthenticated ? (
//                 <OrdersPage />
//               ) : (
//                 <LoginPage
//                   onLogin={(username) => {
//                     setIsAuthenticated(true);
//                     setUsername(username);
//                   }}
//                 />
//               )
//             }
//           />
//         </Routes>
//       </div>
//     </>

//   );
// }

// function App() {
//   const [isAuthenticated, setIsAuthenticated] = useState(false);
//   const [username, setUsername] = useState("");

//   useEffect(() => {
//     // Check cookie for username
//     const match = document.cookie.match(/(?:^|; )username=([^;]*)/);
//     if (match && match[1]) {
//       setIsAuthenticated(true);
//       setUsername(match[1]);
//     }
//   }, []);

//   return (
//     <Router>
//       <AppRoutes
//         isAuthenticated={isAuthenticated}
//         setIsAuthenticated={setIsAuthenticated}
//         setUsername={setUsername}
//       />
//     </Router>
//   );
// }

// export default App;
